import React, { useState } from "react";
import { Check, X, ExternalLink, Star, ArrowRight } from "lucide-react";
import { packagesData } from "../data/mock";

const printerOptions = [
  { id: "form-4", label: "Form 4" },
  { id: "form-4b", label: "Form 4B" },
  { id: "form-4l", label: "Form 4L" },
  { id: "form-4bl", label: "Form 4BL" },
];

export const PackagesSection = () => {
  const [selectedPrinter, setSelectedPrinter] = useState("form-4");
  const data = packagesData[selectedPrinter];

  if (!data) return null;

  return (
    <section id="packages" className="py-24 lg:py-32 bg-[#080808] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-[#F76B1C]/[0.015] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#F76B1C]/[0.01] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
            Packages
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Choose Your Package
          </h2>
          <p className="mt-6 text-lg text-white/45 leading-relaxed">
            Every Formlabs printer is available in three configurations. Start
            with just the printer, or get the complete workflow from day one.
          </p>
        </div>

        {/* Printer selector */}
        <div className="flex items-center justify-center gap-2 mb-12 lg:mb-16 flex-wrap">
          {printerOptions.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSelectedPrinter(opt.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                selectedPrinter === opt.id
                  ? "bg-[#F76B1C] text-white shadow-lg shadow-[#F76B1C]/20"
                  : "bg-white/[0.04] text-white/50 hover:text-white hover:bg-white/[0.08] border border-white/[0.06]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Package tiers */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6" key={selectedPrinter}>
          {data.tiers.map((tier, i) => (
            <div
              key={tier.name}
              className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                tier.recommended
                  ? "bg-[#111111] border-2 border-[#F76B1C]/40 shadow-xl shadow-[#F76B1C]/[0.06]"
                  : "bg-[#111111] border border-white/[0.06]"
              }`}
            >
              {/* Recommended badge */}
              {tier.recommended && (
                <div className="bg-[#F76B1C] px-4 py-2 flex items-center justify-center gap-2">
                  <Star size={14} className="text-white fill-white" />
                  <span className="text-white text-xs font-bold uppercase tracking-wider">
                    Recommended
                  </span>
                </div>
              )}

              {/* Product image */}
              <div className="relative h-44 lg:h-52 bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                <div className="absolute w-[150px] h-[150px] bg-[#F76B1C]/[0.04] rounded-full blur-[60px]" />
                <a
                  href={tier.storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative z-10 group/img"
                >
                  <img loading="lazy"
                    src={tier.image}
                    alt={`${data.name} ${tier.name}`}
                    className="h-32 lg:h-40 w-auto object-contain group-hover/img:scale-105 transition-transform duration-300 drop-shadow-[0_8px_30px_rgba(247,107,28,0.06)]"
                  />
                </a>
              </div>

              {/* Header */}
              <div className={`p-6 lg:p-8 ${tier.recommended ? "" : "pt-8 lg:pt-10"}`}>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {data.name}{" "}
                  <span className="text-white/40 font-medium">
                    {tier.name}
                  </span>
                </h3>
                <p className="text-sm text-white/40 mb-6 leading-relaxed">
                  {tier.description}
                </p>

                {/* Price */}
                <div className="mb-8">
                  <span className="text-3xl lg:text-4xl font-extrabold text-white">
                    {tier.price}
                  </span>
                  {tier.currency && (
                    <span className="text-base text-white/30 ml-2">
                      {tier.currency}
                    </span>
                  )}
                </div>

                {/* CTA */}
                <a
                  href={tier.storeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 mb-8 ${
                    tier.recommended
                      ? "bg-[#F76B1C] hover:bg-[#E05A0F] text-white shadow-lg shadow-[#F76B1C]/20"
                      : "border border-white/[0.1] hover:border-white/[0.2] text-white/70 hover:text-white hover:bg-white/[0.03]"
                  }`}
                >
                  {tier.price === "Contact Us" ? "Get a Quote" : "View in Store"}
                  <ExternalLink size={14} />
                </a>

                {/* Included items */}
                <div className="space-y-3">
                  <span className="text-[10px] text-white/25 font-bold uppercase tracking-widest">
                    What&apos;s Included
                  </span>
                  {tier.items.map((item, j) => (
                    <div
                      key={j}
                      className={`flex items-center gap-3 py-1.5 ${
                        item.included ? "" : "opacity-30"
                      }`}
                    >
                      {item.included ? (
                        <div className="w-5 h-5 rounded-full bg-[#F76B1C]/10 flex items-center justify-center shrink-0">
                          <Check size={12} className="text-[#F76B1C]" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-white/[0.04] flex items-center justify-center shrink-0">
                          <X size={10} className="text-white/20" />
                        </div>
                      )}
                      <span
                        className={`text-sm ${
                          item.included
                            ? "text-white/70"
                            : "text-white/20 line-through"
                        }`}
                      >
                        {item.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-12 text-center">
          <p className="text-white/25 text-xs mb-4">
            All prices are in AUD and exclude shipping. Government and
            education pricing available on request.
          </p>
          <a
            href="#materials"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#materials");
              if (el)
                window.scrollTo({
                  top: el.getBoundingClientRect().top + window.scrollY - 80,
                  behavior: "smooth",
                });
            }}
            className="inline-flex items-center gap-2 text-[#F76B1C] hover:text-[#FF8C42] font-semibold text-sm transition-colors duration-200 group"
          >
            Explore Materials
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform duration-200"
            />
          </a>
        </div>
      </div>
    </section>
  );
};
